import React, { useState } from 'react';
import { Alert, AlertDescription } from './components/ui/alert.tsx';
import { Button } from './components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card.tsx';
import { Input } from './components/ui/input.tsx';
import { Badge } from './components/ui/badge.tsx';
import { Progress } from './components/ui/progress.tsx';
import { Shield, AlertTriangle, CheckCircle, XCircle, Lock, Globe, Server, FileText, Mail, Download, ArrowLeft, Calendar, Phone, Clock, MessageSquare } from 'lucide-react';
import { startScan, pollForResults } from './services/api.ts';
import { Textarea } from './components/ui/textarea.tsx';
import { Select } from './components/ui/select.tsx';

const SecurityScanner = () => {
  const [screen, setScreen] = useState('landing');
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [scanProgress, setScanProgress] = useState(0);
  const [currentCheck, setCurrentCheck] = useState('');
  const [scanResults, setScanResults] = useState(null);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    companyUrl: '',
    preferredTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [sendEmail, setSendEmail] = useState(true); // Email opt-in checkbox
  const [emailStatus, setEmailStatus] = useState<string | null>(null); // Track email quota status
  const turnstileRef = React.useRef(null);
  const turnstileWidgetId = React.useRef<string | null>(null); // Store widgetId for reset
  
  // Reset Turnstile widget - called when returning to landing page
  const resetTurnstile = () => {
    console.log('Resetting Turnstile widget...');
    setTurnstileToken('');
    
    // When navigating back to landing, the old widget is gone from DOM
    // Just need to render a fresh one
    setTimeout(() => {
      if ((window as any).turnstile && turnstileRef.current) {
        console.log('Checking for existing Turnstile widget...');
        
        // Clear any existing widget in the container
        const existingWidget = turnstileRef.current.querySelector('iframe');
        if (existingWidget) {
          console.log('Found existing widget, clearing...');
          turnstileRef.current.innerHTML = '';
        }
        
        console.log('Rendering fresh Turnstile widget...');
        try {
          const widgetId = (window as any).turnstile.render(turnstileRef.current, {
            sitekey: '0x4AAAAAAB424PwWyGi2OWar',
            callback: (token: string) => {
              console.log('✓ NEW Turnstile token received:', token);
              setTurnstileToken(token);
            },
            theme: 'light'
          });
          turnstileWidgetId.current = widgetId;
          console.log('✓ Turnstile widget rendered with ID:', widgetId);
        } catch (error) {
          console.error('Failed to render Turnstile:', error);
        }
      } else {
        console.warn('Turnstile script or ref not available');
      }
    }, 200); // Slightly longer delay to ensure DOM is ready
  };

  // Handle navigation from email links (e.g., #consultation)
  React.useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'consultation') {
      setScreen('consultation');
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // Setup Turnstile callback and render widget
  React.useEffect(() => {
    (window as any).onTurnstileCallback = (token: string) => {
      console.log('Turnstile token received:', token);
      setTurnstileToken(token);
    };

    // Explicitly render Turnstile widget once script loads
    const renderTurnstile = () => {
      if ((window as any).turnstile && turnstileRef.current) {
        const existingWidget = turnstileRef.current.querySelector('iframe');
        if (!existingWidget) {
          console.log('Rendering Turnstile widget...');
          const widgetId = (window as any).turnstile.render(turnstileRef.current, {
            sitekey: '0x4AAAAAAB424PwWyGi2OWar',
            callback: (token: string) => {
              console.log('Turnstile token received:', token);
              setTurnstileToken(token);
            },
            theme: 'light'
          });
          // Store widgetId for reset functionality
          turnstileWidgetId.current = widgetId;
          console.log('Turnstile widgetId stored:', widgetId);
        }
      } else {
        // Retry if script not loaded yet
        setTimeout(renderTurnstile, 100);
      }
    };

    // Wait a bit for script to load, then render
    const timer = setTimeout(renderTurnstile, 500);
    return () => clearTimeout(timer);
  }, []);

  const checks = [
    'Checking SSL/TLS configuration...',
    'Analyzing security headers...',
    'Examining SPF records...',
    'Validating DKIM authentication...',
    'Checking DMARC policy...',
    'Testing for exposed admin panels...',
    'Checking cookie security...',
    'Scanning for mixed content...',
    'Analyzing server information...',
    'Checking subdomain exposure...',
    'Validating certificate chain...',
    'Testing HTTPS enforcement...',
    'Analyzing robots.txt security...',
    'Checking for .git directory exposure...',
    'Scanning for .env file leakage...',
    'Testing for exposed backup files...'
  ];

  const performScan = async (scanUrl = url) => {
    setScreen('scanning');
    setScanProgress(0);
    setCurrentCheck(checks[0]);
    setEmailStatus(null); // Reset email status

    // Email is optional now - only send if checkbox is checked and email provided
    const finalEmail = (sendEmail && email.trim()) ? email : '';

    try {
      // Start the scan
      console.log('Starting scan for:', scanUrl);
      const scanResponse = await startScan({
        url: scanUrl,
        email: finalEmail,
        companySize: 'not-specified',
        turnstileToken
      });

      console.log('Scan started:', scanResponse);
      
      // Track email status from response
      if (scanResponse.emailStatus) {
        setEmailStatus(scanResponse.emailStatus);
      }

      // Animate through all checks smoothly
      let checkIndex = 0;
      const totalChecks = checks.length;
      const checkDuration = 400; // ms per check (12 checks * 400ms = 4.8 seconds)
      
      const checkInterval = setInterval(() => {
        checkIndex++;
        if (checkIndex < totalChecks) {
          setCurrentCheck(checks[checkIndex]);
          setScanProgress(Math.round((checkIndex / totalChecks) * 100));
        } else {
          clearInterval(checkInterval);
        }
      }, checkDuration);

      // Poll for results (but don't update progress - let animation control it)
      const results = await pollForResults(
        scanResponse.scanId,
        scanResponse.accessToken,
        () => {} // Don't update progress from polling
      );

      console.log('Scan complete:', results);

      // Ensure all checks are shown before transitioning
      const remainingTime = Math.max(0, (totalChecks - checkIndex) * checkDuration);
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      // Clear interval and show final state
      clearInterval(checkInterval);
      setScanProgress(100);
      setCurrentCheck(checks[totalChecks - 1]);
      
      // Brief pause on 100% before showing results
      await new Promise(resolve => setTimeout(resolve, 500));

      // Set final results
      setScanResults(results);
      setScreen('results');

    } catch (error) {
      console.error('Scan failed:', error);
      
      // Show error to user
      alert(`Scan failed: ${error.message}. Please try again or contact support.`);
      setScreen('landing');
      resetTurnstile(); // Reset Turnstile on error so user can retry
    }
  };

  // BACKUP: Old mock scan function (commented out)
  /*
  const performScanMock = async () => {
    setScreen('scanning');
    setScanProgress(0);

    for (let i = 0; i < checks.length; i++) {
      setCurrentCheck(checks[i]);
      setScanProgress(((i + 1) / checks.length) * 100);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    const isHttps = url.startsWith('https://');
    
    const results = {
      domain: url.replace(/^https?:\/\//, '').replace(/\/$/, ''),
      scanDate: new Date().toLocaleDateString(),
      score: isHttps ? 72 : 45,
      grade: isHttps ? 'C' : 'F',
      critical: isHttps ? 2 : 4,
      warnings: 5,
      passed: isHttps ? 8 : 4,
      issues: [
        {
          severity: 'critical',
          title: 'SSL Certificate Expiring Soon',
          description: 'Your SSL certificate expires in 12 days',
          risk: 'Visitors will see security warnings, causing loss of trust and potential revenue',
          recommendation: 'Renew SSL certificate immediately',
          fixTime: 'Quick fix (< 1 hour)',
          show: isHttps
        },
        {
          severity: 'critical',
          title: 'No HTTPS Enforcement',
          description: 'Website is accessible via insecure HTTP protocol',
          risk: 'Data transmitted between users and your server can be intercepted',
          recommendation: 'Implement HTTPS redirect and obtain SSL certificate',
          fixTime: 'Moderate (2-4 hours)',
          show: !isHttps
        },
        {
          severity: 'critical',
          title: 'Missing Security Headers',
          description: 'Critical headers like X-Frame-Options and CSP not configured',
          risk: 'Site vulnerable to clickjacking and XSS attacks',
          recommendation: 'Configure Content-Security-Policy and X-Frame-Options headers',
          fixTime: 'Quick fix (< 1 hour)',
          show: true
        },
        {
          severity: 'critical',
          title: 'Admin Panel Publicly Accessible',
          description: 'Login page found at /admin without additional protection',
          risk: 'Attackers can attempt brute force attacks on administrative access',
          recommendation: 'Implement IP whitelist or 2FA for admin access',
          fixTime: 'Moderate (2-4 hours)',
          show: true
        },
        {
          severity: 'warning',
          title: 'Outdated TLS Protocol',
          description: 'TLS 1.0 and 1.1 still enabled on server',
          risk: 'Older protocols have known vulnerabilities',
          recommendation: 'Disable TLS 1.0/1.1, enforce TLS 1.2+',
          fixTime: 'Quick fix (< 1 hour)',
          show: isHttps
        },
        {
          severity: 'warning',
          title: 'Weak Cookie Security',
          description: 'Session cookies missing HttpOnly and SameSite flags',
          risk: 'Cookies vulnerable to XSS and CSRF attacks',
          recommendation: 'Add HttpOnly, Secure, and SameSite=Strict flags',
          fixTime: 'Quick fix (< 1 hour)',
          show: true
        },
        {
          severity: 'warning',
          title: 'Server Version Exposed',
          description: 'Server headers reveal exact software versions',
          risk: 'Attackers can target known vulnerabilities in specific versions',
          recommendation: 'Configure server to hide version information',
          fixTime: 'Quick fix (< 1 hour)',
          show: true
        },
        {
          severity: 'warning',
          title: 'Incomplete DKIM Configuration',
          description: 'Only one DKIM selector found. Best practice requires redundant selectors for email authentication',
          risk: 'Single selector configuration reduces email deliverability redundancy and may impact authentication during key rotation',
          recommendation: 'Configure multiple DKIM selectors through your email provider (M365, Google Workspace, etc.) and add corresponding DNS records',
          fixTime: 'Quick fix (< 1 hour)',
          show: isHttps
        },
        {
          severity: 'warning',
          title: 'Missing SPF Record',
          description: 'No SPF record found for email authentication',
          risk: 'Your domain can be spoofed for phishing attacks',
          recommendation: 'Add SPF TXT record to DNS (e.g., v=spf1 include:spf.protection.outlook.com -all for M365 or include:_spf.google.com for Google)',
          fixTime: 'Quick fix (< 1 hour)',
          show: true
        },
        {
          severity: 'warning',
          title: 'No DMARC Policy',
          description: 'DMARC record not found - email authentication policy missing',
          risk: 'Without DMARC, you cannot monitor or prevent domain spoofing attempts. This affects email deliverability and brand protection',
          recommendation: 'Add DMARC TXT record (_dmarc.yourdomain.com) starting with monitoring mode (v=DMARC1; p=none; rua=mailto:dmarc@yourdomain.com)',
          fixTime: 'Quick fix (< 1 hour)',
          show: !isHttps
        },
        {
          severity: 'warning',
          title: 'Exposed Directory Listing',
          description: 'Directory browsing enabled on /uploads/',
          risk: 'Sensitive files may be accessible to unauthorized users',
          recommendation: 'Disable directory listing in web server config',
          fixTime: 'Quick fix (< 1 hour)',
          show: true
        }
      ].filter(issue => issue.show),
      passed_checks: [
        'Valid SSL certificate installed',
        'HTTPS enforced site-wide',
        'Strong cipher suites configured',
        'No mixed content detected',
        'HSTS header present',
        'X-Content-Type-Options configured',
        'DKIM email authentication properly configured',
        'Valid SPF record for email authentication'
      ]
    };

    setScanResults(results);
    setScreen('results');
  };
  */

  const handleSubmit = () => {
    // Only require email if sendEmail checkbox is checked
    if (url && (!sendEmail || email)) {
      // Auto-add https:// if not present
      let formattedUrl = url.trim();
      if (!formattedUrl.match(/^https?:\/\//i)) {
        formattedUrl = 'https://' + formattedUrl;
        setUrl(formattedUrl);
      }
      // Pass the formatted URL directly to performScan
      performScan(formattedUrl);
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'destructive';
      case 'warning': return 'default';
      default: return 'secondary';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <XCircle className="h-5 w-5" />;
      case 'warning': return <AlertTriangle className="h-5 w-5" />;
      default: return <CheckCircle className="h-5 w-5" />;
    }
  };

  const getGradeColor = (grade) => {
    switch(grade) {
      case 'A':
      case 'B': return 'text-green-600';
      case 'C': return 'text-yellow-600';
      case 'D': return 'text-orange-600';
      case 'F': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  // Landing Screen
  if (screen === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div className="flex justify-center mb-4">
              <div>
                <h1 className="text-4xl font-bold mb-2 text-slate-900">
                  Website and Email Security Scan
                </h1>
                <div className="text-sm text-slate-600 text-right">
                  By{' '}
                  <a 
                    href="https://internacious.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    internacious
                  </a>
                </div>
              </div>
            </div>
            <p className="text-xl text-slate-600 mb-2">
              Discover Vulnerabilities Before Hackers Do
            </p>
            <p className="text-sm text-slate-500">
              Comprehensive analysis in 60 seconds • No credit card required
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Scan Your Website</CardTitle>
              <CardDescription>
                We'll check 12 critical website and email security points and provide a detailed report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Website URL
                </label>
                <Input
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Business Email (Optional)
                </label>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="text-base"
                  disabled={!sendEmail}
                />
                <div className="flex items-center mt-2 gap-2">
                  <input
                    type="checkbox"
                    id="sendEmailCheckbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="sendEmailCheckbox" className="text-xs text-slate-600 cursor-pointer">
                    Email me the detailed security report
                  </label>
                </div>
                {!sendEmail && (
                  <p className="text-xs text-slate-500 mt-1">
                    Results will be viewable in your browser
                  </p>
                )}
                {sendEmail && (
                  <p className="text-xs text-slate-500 mt-1">
                    Limit: 25 scans per day per email address
                  </p>
                )}
              </div>

              <div className="flex justify-center">
                <div 
                  ref={turnstileRef}
                  className="cf-turnstile"
                ></div>
              </div>

              {!turnstileToken && url && (
                <p className="text-xs text-amber-600 text-center">
                  Verifying you're human... (Button will enable shortly)
                </p>
              )}

              <Button 
                onClick={handleSubmit}
                className="w-full text-base py-6"
                size="lg"
                disabled={!url || !turnstileToken || (sendEmail && !email.trim())}
              >
                <Shield className="mr-2 h-5 w-5" />
                Scan My Website
              </Button>

              <div className="flex items-center gap-4 pt-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>60 second scan</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Detailed PDF report</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What We Check */}
          <Card className="mt-12">
            <CardHeader>
              <CardTitle className="text-center">What We Check</CardTitle>
              <CardDescription className="text-center">
                Our website and email scans analyze 16 critical security points
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">SSL/TLS Configuration</div>
                    <div className="text-sm text-slate-600">Certificate validity, expiration, and encryption strength</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Security Headers</div>
                    <div className="text-sm text-slate-600">CSP, X-Frame-Options, HSTS, and more</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">SPF Records</div>
                    <div className="text-sm text-slate-600">Email sender authentication and anti-spoofing</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">DKIM Configuration</div>
                    <div className="text-sm text-slate-600">Email authentication for all providers (M365, Google Workspace, SendGrid, etc.)</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">DMARC Policy</div>
                    <div className="text-sm text-slate-600">Email authentication policy and reporting</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Admin Panel Exposure</div>
                    <div className="text-sm text-slate-600">Publicly accessible login pages and admin areas</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Cookie Security</div>
                    <div className="text-sm text-slate-600">HttpOnly, Secure, and SameSite flags</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Mixed Content Detection</div>
                    <div className="text-sm text-slate-600">HTTP resources on HTTPS pages</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Server className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Server Information</div>
                    <div className="text-sm text-slate-600">Version disclosure and technology stack exposure</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Subdomain Enumeration</div>
                    <div className="text-sm text-slate-600">Forgotten or unmonitored subdomains</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Certificate Chain</div>
                    <div className="text-sm text-slate-600">Complete validation of SSL certificate chain</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">HTTPS Enforcement</div>
                    <div className="text-sm text-slate-600">Secure connection requirements and redirects</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">robots.txt Security</div>
                    <div className="text-sm text-slate-600">Sensitive path disclosure in search engine directives</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">.git Directory Exposure</div>
                    <div className="text-sm text-slate-600">Publicly accessible source code and commit history</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">.env File Exposure</div>
                    <div className="text-sm text-slate-600">Database credentials and API keys leakage</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Server className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-slate-900">Backup File Exposure</div>
                    <div className="text-sm text-slate-600">Database dumps and backup archives accessibility</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-slate-600 text-sm">
            <p className="mb-2">© {new Date().getFullYear()} Security Scanner. All rights reserved.</p>
            <p>
              Built by{' '}
              <a 
                href="https://internacious.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                internacious
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Scanning Screen
  if (screen === 'scanning') {
    const checkDetails = [
      { name: 'SSL/TLS Configuration', icon: Lock },
      { name: 'Security Headers', icon: Shield },
      { name: 'SPF Records', icon: Mail },
      { name: 'DKIM Email Authentication', icon: Globe },
      { name: 'DMARC Policy', icon: Shield },
      { name: 'Exposed Admin Panels', icon: AlertTriangle },
      { name: 'Cookie Security', icon: Shield },
      { name: 'Mixed Content', icon: Lock },
      { name: 'Server Information', icon: Server },
      { name: 'Subdomain Exposure', icon: Globe },
      { name: 'Certificate Chain', icon: Lock },
      { name: 'HTTPS Enforcement', icon: Shield },
      { name: 'robots.txt Security', icon: FileText },
      { name: '.git Directory Check', icon: AlertTriangle },
      { name: '.env File Check', icon: Lock },
      { name: 'Backup File Detection', icon: Server }
    ];

    const currentCheckIndex = Math.floor((scanProgress / 100) * checks.length);

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4 mx-auto">
                <Shield className="h-8 w-8 text-white animate-pulse" />
              </div>
              <CardTitle className="text-2xl">Scanning Your Website</CardTitle>
              <CardDescription className="text-base">
                Analyzing {url.replace(/^https?:\/\//, '')} for security vulnerabilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Overall Progress</span>
                  <span className="font-medium text-slate-900">{Math.round(scanProgress)}%</span>
                </div>
                <Progress value={scanProgress} className="h-2" />
              </div>

              <div className="space-y-3 pt-2">
                {checkDetails.map((check, idx) => {
                  const isComplete = idx < currentCheckIndex;
                  const isCurrent = idx === currentCheckIndex;
                  const isPending = idx > currentCheckIndex;
                  const Icon = check.icon;

                  return (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                        isComplete 
                          ? 'bg-green-50 border border-green-200' 
                          : isCurrent 
                          ? 'bg-blue-50 border border-blue-200 shadow-sm' 
                          : 'bg-slate-50 border border-slate-200 opacity-60'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {isComplete && (
                          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-white" />
                          </div>
                        )}
                        {isCurrent && (
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                            <div className="h-4 w-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                          </div>
                        )}
                        {isPending && (
                          <div className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${
                          isComplete 
                            ? 'text-green-900' 
                            : isCurrent 
                            ? 'text-blue-900' 
                            : 'text-slate-500'
                        }`}>
                          {check.name}
                        </div>
                        {isCurrent && (
                          <div className="text-xs text-blue-700 mt-0.5">
                            Scanning now...
                          </div>
                        )}
                        {isComplete && (
                          <div className="text-xs text-green-700 mt-0.5">
                            Complete
                          </div>
                        )}
                      </div>
                      {isComplete && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-300">
                          Done
                        </Badge>
                      )}
                      {isCurrent && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-300">
                          Active
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Results Screen
  if (screen === 'results') {
    const criticalIssues = scanResults.issues.filter(i => i.severity === 'critical');
    const warningIssues = scanResults.issues.filter(i => i.severity === 'warning');
    const informationalIssues = scanResults.issues.filter(i => i.severity === 'informational');
    
    // Group issues by category
    const websiteIssues = scanResults.issues.filter(i => i.category === 'website');
    const emailIssues = scanResults.issues.filter(i => i.category === 'email');
    
    console.log('DEBUG - Total issues:', scanResults.issues.length);
    console.log('DEBUG - Critical issues:', criticalIssues.length);
    console.log('DEBUG - Warning issues:', warningIssues.length);
    console.log('DEBUG - Informational issues:', informationalIssues.length, informationalIssues);
    console.log('DEBUG - Website issues:', websiteIssues.length);
    console.log('DEBUG - Email issues:', emailIssues.length);

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Email quota warning banner */}
          {emailStatus === 'quota_reached' && (
            <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <p className="text-sm text-amber-800">
                  <strong>Email quota reached today.</strong> Your scan completed successfully and results are shown below. Email reports will resume tomorrow.
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => {
                setScreen('landing');
                resetTurnstile();
              }}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              New Scan
            </Button>
            
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Security Report
                </h1>
                <p className="text-slate-600">
                  {scanResults.domain} • Scanned on {scanResults.scanDate}
                </p>
              </div>
              <Button onClick={() => setScreen('report')} className="w-full md:w-auto">
                <Download className="mr-2 h-4 w-4" />
                View Full Report
              </Button>
            </div>
          </div>

          {/* Score Card */}
          <Card className="mb-8 shadow-lg border-2">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="md:col-span-1 text-center md:border-r">
                  <div className={`text-6xl font-bold mb-2 ${getGradeColor(scanResults.grade)}`}>
                    {scanResults.grade}
                  </div>
                  <div className="text-2xl font-semibold text-slate-700 mb-1">
                    {scanResults.score}/100
                  </div>
                  <div className="text-sm text-slate-500">Overall Security Score</div>
                </div>
                
                <div className="md:col-span-3 grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <XCircle className="h-6 w-6 text-red-600" />
                      <div className="text-3xl font-bold text-red-600">
                        {scanResults.critical}
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">Critical Issues</div>
                    <div className="text-xs text-slate-500 mt-1">Require immediate action</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <AlertTriangle className="h-6 w-6 text-yellow-600" />
                      <div className="text-3xl font-bold text-yellow-600">
                        {scanResults.warnings}
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">Warnings</div>
                    <div className="text-xs text-slate-500 mt-1">Should be addressed soon</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Globe className="h-6 w-6 text-blue-600" />
                      <div className="text-3xl font-bold text-blue-600">
                        {informationalIssues.length}
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">Informational</div>
                    <div className="text-xs text-slate-500 mt-1">For your awareness</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <div className="text-3xl font-bold text-green-600">
                        {scanResults.passed}
                      </div>
                    </div>
                    <div className="text-sm text-slate-600">Checks Passed</div>
                    <div className="text-xs text-slate-500 mt-1">Good security practices</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Website Security */}
            <Card className="shadow-md">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Website Security
                </CardTitle>
                <CardDescription>
                  {websiteIssues.length} finding{websiteIssues.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {websiteIssues.length === 0 ? (
                  <div className="text-sm text-slate-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    All website security checks passed
                  </div>
                ) : (
                  <div className="space-y-2">
                    {websiteIssues.slice(0, 3).map((issue, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className={`mt-1 ${
                          issue.severity === 'critical' ? 'text-red-600' : 
                          issue.severity === 'warning' ? 'text-yellow-600' : 
                          'text-blue-600'
                        }`}>
                          {issue.severity === 'critical' ? <XCircle className="h-4 w-4" /> : 
                           issue.severity === 'warning' ? <AlertTriangle className="h-4 w-4" /> : 
                           <Globe className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{issue.title}</div>
                        </div>
                      </div>
                    ))}
                    {websiteIssues.length > 3 && (
                      <div className="text-xs text-slate-500 mt-2">
                        +{websiteIssues.length - 3} more finding{websiteIssues.length - 3 !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Security */}
            <Card className="shadow-md">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-purple-600" />
                  Email Security
                </CardTitle>
                <CardDescription>
                  {emailIssues.length} finding{emailIssues.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {emailIssues.length === 0 ? (
                  <div className="text-sm text-slate-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    All email security checks passed
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emailIssues.slice(0, 3).map((issue, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className={`mt-1 ${
                          issue.severity === 'critical' ? 'text-red-600' : 
                          issue.severity === 'warning' ? 'text-yellow-600' : 
                          'text-blue-600'
                        }`}>
                          {issue.severity === 'critical' ? <XCircle className="h-4 w-4" /> : 
                           issue.severity === 'warning' ? <AlertTriangle className="h-4 w-4" /> : 
                           <Globe className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{issue.title}</div>
                        </div>
                      </div>
                    ))}
                    {emailIssues.length > 3 && (
                      <div className="text-xs text-slate-500 mt-2">
                        +{emailIssues.length - 3} more finding{emailIssues.length - 3 !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Critical Issues */}
          {criticalIssues.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <XCircle className="h-6 w-6 text-red-600" />
                Critical Issues
              </h2>
              <div className="space-y-4">
                {criticalIssues.map((issue, idx) => (
                  <Card key={idx} className="border-l-4 border-l-red-600">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="destructive">Critical</Badge>
                          </div>
                          <CardTitle className="text-lg mb-2">{issue.title}</CardTitle>
                          <CardDescription className="text-base">
                            {issue.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Business Risk:</div>
                        <div className="text-sm text-slate-600">{issue.risk}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Recommendation:</div>
                        <div className="text-sm text-slate-600">{issue.recommendation}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {warningIssues.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
                Warnings
              </h2>
              <div className="space-y-4">
                {warningIssues.map((issue, idx) => (
                  <Card key={idx} className="border-l-4 border-l-yellow-600">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge>Warning</Badge>
                          </div>
                          <CardTitle className="text-lg mb-2">{issue.title}</CardTitle>
                          <CardDescription className="text-base">
                            {issue.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Business Risk:</div>
                        <div className="text-sm text-slate-600">{issue.risk}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Recommendation:</div>
                        <div className="text-sm text-slate-600">{issue.recommendation}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Informational Issues */}
          {informationalIssues.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-600" />
                Informational Items
              </h2>
              <div className="space-y-4">
                {informationalIssues.map((issue, idx) => (
                  <Card key={idx} className="border-l-4 border-l-blue-600">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary">Informational</Badge>
                          </div>
                          <CardTitle className="text-lg mb-2">{issue.title}</CardTitle>
                          <CardDescription className="text-base">
                            {issue.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Risk:</div>
                        <div className="text-sm text-slate-600">{issue.risk}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">Recommendation:</div>
                        <div className="text-sm text-slate-600">{issue.recommendation}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Passed Checks */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-600" />
              What You're Doing Right
            </h2>
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {scanResults.passed_checks.map((check, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>{check}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Need Help Fixing These or Other Issues?
                </h3>
                <p className="text-slate-600 mb-6">
                  Our security experts can help you address these and other vulnerabilities to protect your business
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="text-base" onClick={() => setScreen('consultation')}>
                    <Calendar className="mr-2 h-5 w-5" />
                    Schedule Free Consultation
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => setScreen('report')} className="text-base">
                    <FileText className="mr-2 h-5 w-5" />
                    Download Full Report
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-slate-600 text-sm">
            <p className="mb-2">© {new Date().getFullYear()} Security Scanner. All rights reserved.</p>
            <p>
              By{' '}
              <a 
                href="https://internacious.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                internacious
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Report View
  if (screen === 'report') {
    const criticalIssues = scanResults.issues.filter(i => i.severity === 'critical');
    const warningIssues = scanResults.issues.filter(i => i.severity === 'warning');
    const informationalIssues = scanResults.issues.filter(i => i.severity === 'informational');
    
    // Group issues by category
    const websiteIssues = scanResults.issues.filter(i => i.category === 'website');
    const emailIssues = scanResults.issues.filter(i => i.category === 'email');
    
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-6 flex justify-between items-center print:hidden">
            <Button 
              variant="ghost" 
              onClick={() => setScreen('results')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Results
            </Button>
            <Button onClick={() => window.print()}>
              <Download className="mr-2 h-4 w-4" />
              Print / Save PDF
            </Button>
          </div>

          {/* Report Content */}
          <div className="bg-white border rounded-lg p-8 print:border-0">
            {/* Header */}
            <div className="border-b pb-6 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">
                    Website Security Report
                  </h1>
                  <div className="text-slate-600">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="h-4 w-4" />
                      <span>{scanResults.domain}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Scan Date: {scanResults.scanDate}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-600 mb-1">Provided by:</div>
                  <div className="font-semibold text-lg">
                    <a 
                      href="https://internacious.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      internacious
                    </a>
                  </div>
                  <div className="text-sm text-slate-600">
                    <a 
                      href="https://internacious.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-blue-600"
                    >
                      Connect with internacious
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Executive Summary */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Executive Summary</h2>
              <div className="bg-slate-50 rounded-lg p-6">
                <div className="flex items-center gap-6 mb-4">
                  <div>
                    <div className={`text-5xl font-bold ${getGradeColor(scanResults.grade)}`}>
                      {scanResults.grade}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">Overall Grade</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-3xl font-bold text-slate-900 mb-1">
                      {scanResults.score}/100
                    </div>
                    <div className="text-sm text-slate-600">Security Score</div>
                  </div>
                </div>
                <p className="text-slate-700">
                  Our comprehensive security scan identified <strong>{scanResults.critical} critical vulnerabilities</strong>, <strong>{scanResults.warnings} warnings</strong>, and <strong>{informationalIssues.length} informational items</strong>. 
                  {scanResults.critical > 0 ? ' Immediate action is recommended to address critical security gaps.' : ' Your website demonstrates good security practices in most areas.'}
                </p>
              </div>
            </div>

            {/* Top Priorities */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Top 3 Priorities</h2>
              <div className="space-y-4">
                {scanResults.issues.slice(0, 3).map((issue, idx) => (
                  <div key={idx} className="border-l-4 border-l-red-600 pl-4 py-2">
                    <div className="font-semibold text-slate-900 mb-1">
                      {idx + 1}. {issue.title}
                    </div>
                    <div className="text-sm text-slate-600 mb-2">{issue.description}</div>
                    <div className="text-sm">
                      <span className="font-medium">Fix Time:</span> {issue.fixTime}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Security Findings by Category</h2>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold">Website Security</h3>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{websiteIssues.length}</div>
                  <div className="text-sm text-slate-600">finding{websiteIssues.length !== 1 ? 's' : ''}</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold">Email Security</h3>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{emailIssues.length}</div>
                  <div className="text-sm text-slate-600">finding{emailIssues.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
            </div>

            {/* Detailed Findings */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Detailed Findings</h2>
              
              {/* Critical Issues */}
              {criticalIssues.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-red-600 mb-3">Critical Issues ({criticalIssues.length})</h3>
                  <div className="space-y-4">
                    {criticalIssues.map((issue, idx) => (
                      <div key={idx} className="border border-red-200 bg-red-50 rounded-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {getSeverityIcon(issue.severity)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-slate-900">{issue.title}</h3>
                              <Badge variant={getSeverityColor(issue.severity)}>
                                CRITICAL
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">{issue.description}</p>
                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-slate-700">Risk:</span>
                                <p className="text-slate-600 mt-1">{issue.risk}</p>
                              </div>
                              <div>
                                <span className="font-medium text-slate-700">Recommendation:</span>
                                <p className="text-slate-600 mt-1">{issue.recommendation}</p>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-red-200">
                              <span className="font-medium text-slate-700 text-sm">Estimated Fix Time:</span>
                              <span className="text-slate-600 text-sm ml-2">{issue.fixTime}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning Issues */}
              {warningIssues.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-yellow-600 mb-3">Warnings ({warningIssues.length})</h3>
                  <div className="space-y-4">
                    {warningIssues.map((issue, idx) => (
                      <div key={idx} className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {getSeverityIcon(issue.severity)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-slate-900">{issue.title}</h3>
                              <Badge variant={getSeverityColor(issue.severity)}>
                                WARNING
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">{issue.description}</p>
                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-slate-700">Risk:</span>
                                <p className="text-slate-600 mt-1">{issue.risk}</p>
                              </div>
                              <div>
                                <span className="font-medium text-slate-700">Recommendation:</span>
                                <p className="text-slate-600 mt-1">{issue.recommendation}</p>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-yellow-200">
                              <span className="font-medium text-slate-700 text-sm">Estimated Fix Time:</span>
                              <span className="text-slate-600 text-sm ml-2">{issue.fixTime}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Informational Issues */}
              {informationalIssues.length > 0 && (
                <div className="mb-6" style={{border: '3px solid blue', padding: '20px', backgroundColor: '#e0f2fe'}}>
                  <h3 className="text-lg font-semibold text-blue-600 mb-3" style={{fontSize: '24px'}}>
                    🔵 Informational ({informationalIssues.length})
                  </h3>
                  <div className="space-y-4">
                    {informationalIssues.map((issue, idx) => (
                      <div key={idx} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {getSeverityIcon(issue.severity)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-slate-900">{issue.title}</h3>
                              <Badge variant={getSeverityColor(issue.severity)}>
                                INFO
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-3">{issue.description}</p>
                            <div className="grid md:grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="font-medium text-slate-700">Risk:</span>
                                <p className="text-slate-600 mt-1">{issue.risk}</p>
                              </div>
                              <div>
                                <span className="font-medium text-slate-700">Recommendation:</span>
                                <p className="text-slate-600 mt-1">{issue.recommendation}</p>
                              </div>
                            </div>
                            {issue.fixTime && issue.fixTime !== 'N/A' && (
                              <div className="mt-3 pt-3 border-t border-blue-200">
                                <span className="font-medium text-slate-700 text-sm">Estimated Fix Time:</span>
                                <span className="text-slate-600 text-sm ml-2">{issue.fixTime}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Recommended Next Steps</h2>
              <ol className="space-y-3 text-slate-700">
                <li className="flex gap-3">
                  <span className="font-bold">1.</span>
                  <span>Address critical vulnerabilities within the next 48 hours</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold">2.</span>
                  <span>Schedule fixes for warning-level issues within 2 weeks</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold">3.</span>
                  <span>Implement regular security scanning (monthly recommended)</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold">4.</span>
                  <span>Consider a full penetration test for comprehensive security assessment</span>
                </li>
              </ol>
              
              <div className="mt-6 pt-6 border-t border-blue-200">
                <h3 className="font-semibold text-slate-900 mb-2">How We Can Help</h3>
                <p className="text-slate-700 mb-4">
                  Our team specializes in fixing these exact security issues. We can have your critical vulnerabilities resolved within 24-48 hours.
                </p>
                <div className="text-sm text-slate-600">
                  <div className="mb-1"><strong>Contact:</strong> support@internacious.com</div>
                  <div><strong>Phone:</strong> 02 8313 0464</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t text-center text-sm text-slate-600">
              <p className="mb-2">© {new Date().getFullYear()} Security Scanner. All rights reserved.</p>
              <p>
                By{' '}
                <a 
                  href="https://internacious.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  internacious
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Consultation Form Screen
  if (screen === 'consultation') {
    const handleConsultationSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/consultation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...consultationForm,
            scanResults: scanResults ? {
              domain: scanResults.domain,
              score: scanResults.score,
              grade: scanResults.grade,
              critical: scanResults.critical,
              warnings: scanResults.warnings
            } : null
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }

        // Use requestAnimationFrame to avoid ResizeObserver errors
        requestAnimationFrame(() => {
          setSubmitSuccess(true);
        });
        
        setTimeout(() => {
          requestAnimationFrame(() => {
            setScreen('landing');
            setSubmitSuccess(false);
            setConsultationForm({
              name: '',
              email: '',
              phone: '',
              companyUrl: '',
              preferredTime: '',
              message: ''
            });
          });
        }, 3000);

      } catch (error) {
        console.error('Form submission failed:', error);
        alert('Failed to submit form. Please try again or contact us directly.');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <Button 
            variant="ghost" 
            onClick={() => setScreen(scanResults ? 'results' : 'landing')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4 mx-auto">
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-3xl">Schedule Free Consultation</CardTitle>
              <CardDescription className="text-base">
                Our security experts are ready to help you address your website vulnerabilities
              </CardDescription>
            </CardHeader>

            <CardContent>
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                  <p className="text-slate-600 mb-4">
                    We've received your consultation request and will contact you within 24 hours.
                  </p>
                  <p className="text-sm text-slate-500">
                    Redirecting you back...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleConsultationSubmit} className="space-y-6">
                  {scanResults && (
                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        <div className="font-medium mb-1">Scan Results Reference</div>
                        <div className="text-sm">
                          {scanResults.domain} • Grade: <strong>{scanResults.grade}</strong> ({scanResults.score}/100)
                          {scanResults.critical > 0 && <> • {scanResults.critical} Critical Issue{scanResults.critical !== 1 ? 's' : ''}</>}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name <span className="text-red-600">*</span>
                      </label>
                      <Input
                        id="name"
                        type="text"
                        required
                        placeholder="John Smith"
                        value={consultationForm.name}
                        onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                        className="w-full"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                          Email Address <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="email"
                            type="email"
                            required
                            placeholder="john@company.com"
                            value={consultationForm.email}
                            onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                          Phone Number <span className="text-red-600">*</span>
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="phone"
                            type="tel"
                            required
                            placeholder="(02) 8313 0464"
                            value={consultationForm.phone}
                            onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="companyUrl" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Website <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="companyUrl"
                          type="url"
                          required
                          placeholder="yourcompany.com"
                          value={consultationForm.companyUrl || (scanResults ? scanResults.domain : '')}
                          onChange={(e) => setConsultationForm({...consultationForm, companyUrl: e.target.value})}
                          onBlur={(e) => {
                            let url = e.target.value.trim();
                            if (url && !url.match(/^https?:\/\//i)) {
                              url = 'https://' + url;
                              setConsultationForm({...consultationForm, companyUrl: url});
                            }
                          }}
                          className="pl-10"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Just enter the domain - we'll add https:// automatically</p>
                    </div>

                    <div>
                      <label htmlFor="preferredTime" className="block text-sm font-medium text-slate-700 mb-2">
                        Preferred Contact Time
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Select
                          id="preferredTime"
                          value={consultationForm.preferredTime}
                          onChange={(e) => setConsultationForm({...consultationForm, preferredTime: e.target.value})}
                          className="pl-10"
                        >
                          <option value="">Select a time preference</option>
                          <option value="morning">Morning (9am - 12pm)</option>
                          <option value="afternoon">Afternoon (12pm - 5pm)</option>
                          <option value="evening">Evening (5pm - 7pm)</option>
                          <option value="anytime">Anytime</option>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                        Additional Details
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Textarea
                          id="message"
                          placeholder="Tell us about your security concerns or specific issues you'd like to discuss..."
                          value={consultationForm.message}
                          onChange={(e) => setConsultationForm({...consultationForm, message: e.target.value})}
                          className="pl-10 min-h-[120px]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-slate-700">
                        <strong className="text-slate-900">What to expect:</strong> Our team will review your security scan results and contact you within 24 hours to discuss solutions tailored to your needs.
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="mr-2 h-4 w-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Calendar className="mr-2 h-5 w-5" />
                        Schedule Free Consultation
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    By submitting this form, you agree to be contacted by our team regarding your security consultation.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-slate-600 text-sm">
            <p className="mb-2">© {new Date().getFullYear()} Security Scanner. All rights reserved.</p>
            <p>
              By{' '}
              <a 
                href="https://internacious.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                internacious
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SecurityScanner;
